import { DateTime } from 'luxon'
import { PatientHistoryType } from 'src/graphql.generated'
import { INTAKE_NOTE_TYPES } from 'src/participantHistory/constants'
import { AdvancedTrackSummary } from '../../api/types'
import { toIso } from '../../format'
import { isNutritionIntakeNote } from '../../panels/patientHistory/eventLogger/advancedNoteKinds'

// Widened for `includes`; a track's intake note names it in `value.trackKey`.
const intakeNoteTypes: readonly string[] = INTAKE_NOTE_TYPES

type HistoryEntry = { type?: string | null; date?: string | null; createdAt?: Date | string | null; value?: unknown }

export type AcceptanceCriterion = { key: 'intakeNote' | 'financialEligibility'; label: string; met: boolean }

const trackKeyOf = (value: unknown): unknown =>
  typeof value === 'object' && value !== null ? (value as { trackKey?: unknown }).trackKey : undefined

const isIntakeNote = (entry: HistoryEntry) =>
  (entry.type != null && intakeNoteTypes.includes(entry.type)) || isNutritionIntakeNote(entry.value)

const loggedSince = (entry: HistoryEntry, openedAt: string) =>
  (toIso(entry.createdAt) ?? entry.date ?? '').slice(0, 10) >= openedAt.slice(0, 10)

// The track being accepted: an enrolment in intake, or a planned `startsOnAcceptance` track not yet opened.
export type AcceptTarget = {
  trackKey: string
  enrollmentId: string | null
  openedAt: string | null
  startsOnAcceptance: boolean
}

// Like Standard's program `acceptanceCriteria`, plus eligibility for a first track (ADR-097, ADR-098).
export const acceptanceCriteriaFor = (
  target: AcceptTarget,
  title: string,
  tracks: AdvancedTrackSummary[],
  history: HistoryEntry[]
): AcceptanceCriterion[] => {
  const intakeNote = target.startsOnAcceptance
    ? history.some((entry) => entry.type === PatientHistoryType.IntakeNotePsychiatry)
    : history.some(
        (entry) =>
          isIntakeNote(entry) &&
          trackKeyOf(entry.value) === target.trackKey &&
          (!target.openedAt || loggedSince(entry, target.openedAt))
      )
  const firstTrack = !tracks.some((other) => other.enrollmentId !== target.enrollmentId && other.startedAt !== null)
  const criteria: AcceptanceCriterion[] = [
    {
      key: 'intakeNote',
      label: target.startsOnAcceptance ? 'Psychiatry intake note' : `Intake note for ${title}`,
      met: intakeNote,
    },
  ]

  if (firstTrack) {
    criteria.push({
      key: 'financialEligibility',
      label: 'Financial eligibility confirmed',
      met: history.some((entry) => entry.type === PatientHistoryType.FinancialEligibilityConfirmed),
    })
  }

  return criteria
}

// The next `count` Mondays after today in UTC, the calendar the service checks a start date against.
export const upcomingMondays = (now = new Date(), count = 8): string[] => {
  const first = DateTime.fromJSDate(now, { zone: 'utc' }).startOf('week').plus({ weeks: 1 })

  return Array.from({ length: count }, (_, index) => first.plus({ weeks: index }).toFormat('yyyy-MM-dd'))
}
